import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import "./Button.css";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual weight. Defaults to `secondary` — `primary` is one per screen. */
  variant?: ButtonVariant;
  /** `md` uses `--fc-control-height`; `sm` is one spacing step shorter. */
  size?: ButtonSize;
  /**
   * Shows a spinner and sets `aria-busy`. The button is disabled while
   * loading and its width never changes: the spinner either replaces the
   * leading icon in place, or is overlaid on a zero-opacity label.
   */
  loading?: boolean;
  /**
   * Square button holding a single glyph. **Requires `aria-label`** — pair it
   * with a `Tooltip` using the exact same string.
   */
  iconOnly?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

function Spinner() {
  return <span className="fc-btn__spinner" aria-hidden="true" />;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "secondary",
      size = "md",
      loading = false,
      iconOnly = false,
      leadingIcon,
      trailingIcon,
      className,
      children,
      disabled,
      type = "button",
      ...rest
    },
    ref,
  ) {
    const hasLeading = leadingIcon !== undefined && leadingIcon !== null;
    const hasTrailing = trailingIcon !== undefined && trailingIcon !== null;
    const hasChildren =
      children !== undefined && children !== null && children !== false;

    // The spinner sits in the leading-icon slot when there is one (identical
    // box, so nothing reflows). Otherwise it is overlaid and the label fades to
    // zero opacity — opacity keeps the label in the accessibility tree, which
    // `visibility: hidden` would not.
    const spinnerInline = loading && (iconOnly || hasLeading);
    const spinnerOverlay = loading && !spinnerInline;

    const classes = [
      "fc-btn",
      `fc-btn--${variant}`,
      `fc-btn--${size}`,
      iconOnly ? "fc-btn--icon" : null,
      spinnerOverlay ? "fc-btn--spinner-overlay" : null,
      loading ? "is-loading" : null,
      className,
    ]
      .filter((value): value is string => Boolean(value))
      .join(" ");

    let inner: ReactNode;
    if (iconOnly) {
      inner = (
        <span className="fc-btn__icon" aria-hidden="true">
          {loading ? <Spinner /> : (leadingIcon ?? children)}
        </span>
      );
    } else {
      inner = (
        <>
          {spinnerInline ? (
            <span className="fc-btn__icon">
              <Spinner />
            </span>
          ) : hasLeading ? (
            <span className="fc-btn__icon" aria-hidden="true">
              {leadingIcon}
            </span>
          ) : null}
          {hasChildren ? (
            <span className="fc-btn__label">{children}</span>
          ) : null}
          {hasTrailing ? (
            <span className="fc-btn__icon" aria-hidden="true">
              {trailingIcon}
            </span>
          ) : null}
        </>
      );
    }

    return (
      <button
        {...rest}
        ref={ref}
        type={type}
        className={classes}
        disabled={disabled === true || loading}
        aria-busy={loading || undefined}
      >
        <span className="fc-btn__content">{inner}</span>
        {spinnerOverlay ? (
          <span className="fc-btn__overlay">
            <Spinner />
          </span>
        ) : null}
      </button>
    );
  },
);
